import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Col, Row } from "reactstrap";
import CustomerInfo from "../../../components/paySteps/CustomerInfo";
import WebpayPostRedirect from "../../../components/paySteps/WebpayPostRedirect";
import { useUserState } from "../../../hooks";
import { t } from "../../../utils/utils";

import useUserStore from "../store/user/userStore";
import DataTable from "./parts/Table";

import { Button } from "@octano/global-ui";
import { format } from "rut.js";
import useStepStore from "../store/step/stepStore";
import AnonymousUserInputs from "./parts/AnonymousUserInputs";
import { PayerRut } from "./types";
import { usePaymentSuppliers } from "../hooks/usePaymentSuppliers";
import PaymentSupplierModal, {
  PaymentSupplierModalMethods,
} from "./parts/PaymentSupplierModal";
import { requestGetDebtsToken } from "../searchStep/api";
import { PP_NOTICE } from "../../../config/constants";
import { Box, CircularProgress } from "@mui/material";

const PaymentStep = () => {
  const { user, debts, setDebts, setUser } = useUserStore((state) => ({
    user: state.user,
    debts: state.debts,
    setDebts: state.setDebts,
    setUser: state.setUser,
  }));
  const { prevStep, setCurrentStep } = useStepStore((state) => ({
    prevStep: state.prevStep,
    setCurrentStep: state.setCurrentStep,
  }));
  const { isLogged } = useUserState();
  const paymentSupplierRef = useRef<PaymentSupplierModalMethods>(null);

  const { suppliers: paymentSuppliers } = usePaymentSuppliers(isLogged);

  const [isLoadingDebts, setIsLoadingDebts] = useState<boolean>(true);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isPayLoading] = useState<boolean>(false);
  const [webpayRedirect] = useState<{
    token: string;
    initURL: string;
  }>();

  const { control, formState, watch } = useForm<PayerRut>({
    defaultValues: {
      payerRut: user?.customerIdentifier,
      payerEmail: "",
    },
    mode: "all",
  });

  const { payerRut, payerEmail } = watch();

  const selectedDebts = useMemo(
    () =>
      debts?.filter(
        (e) =>
          !!e.selected && e?.finalAmount?.value && e?.finalAmount?.value > 0
      ),
    [debts]
  );

  const disablePayment = useMemo(() => {
    if (!selectedDebts?.length || !formState.isValid) {
      return true;
    }

    if (!isLogged && (!payerEmail.length || !payerRut.length)) {
      return true;
    }
    return false;
  }, [selectedDebts, formState.isValid, payerEmail, payerRut, isLogged]);

  const handleInitPayment = useCallback(() => {
    paymentSupplierRef?.current?.open({
      suppliers: paymentSuppliers,
      payerEmail,
      payerIdentifier: payerRut,
      debts: selectedDebts,
    });
  }, [paymentSuppliers, payerEmail, payerRut, selectedDebts]);

  const fetchDebts = useCallback(async () => {
    setIsLoadingDebts(true);
    setDebts([]);
    const { data } = await requestGetDebtsToken();
    const hasUser = data?.customer?.customerIdentifier;
    if (!hasUser) {
      localStorage.removeItem("x-guest");
      setCurrentStep(0);
      return;
    }

    setUser(data.customer.name, String(data.customer.customerIdentifier));
    setDebts(data.debts);

    setIsLoadingDebts(false);
  }, [setUser, setDebts, setCurrentStep]);

  useEffect(() => {
    if (!isMounted) {
      fetchDebts();
      setIsMounted(true);
    }
  }, [isMounted, fetchDebts]);

  if (isLoadingDebts) {
    return (
      <Box
        width="100%"
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        minHeight={"150px"}
        padding={"60px"}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <CustomerInfo rut={format(user?.customerIdentifier)} name={user?.name} />
      <p className="text-left h5 mx-5">{PP_NOTICE}</p>
      <DataTable />
      <AnonymousUserInputs isLogged={isLogged} control={control} />
      <div className="container-max-400 ml-auto my-3 mr-5">
        <Row>
          <Col xs={6} className="pr-1 pr-sm-2">
            <Button
              type="button"
              text={t("cancel")}
              onClick={() => prevStep()}
              outlined
              fullwidth
            />
          </Col>
          <Col xs={6} className="pl-1 pl-sm-2">
            <Button
              type="button"
              text={t("pay")}
              disabled={disablePayment}
              onClick={handleInitPayment}
              loading={isPayLoading}
              fullwidth
            />
          </Col>
          {webpayRedirect ? <WebpayPostRedirect {...webpayRedirect} /> : null}
        </Row>
      </div>

      <PaymentSupplierModal ref={paymentSupplierRef} isLoggedIn={isLogged} />
    </>
  );
};

export default PaymentStep;
